interface MainHeroProps {
  titleHighlight: string
  title: string
  description: string
  emphasis: string
  ctaLabel: string
  onFindCenter: () => void
}

/**
 * #01 Hero — full-bleed photo background with text overlay.
 * Desktop: `main.webp`, Mobile: `main-m.webp`.
 * Figma: node 125:1318 (desktop) / 207:7290 (mobile)
 */
export function MainHero({
  titleHighlight,
  title,
  description,
  emphasis,
  ctaLabel,
  onFindCenter,
}: MainHeroProps) {
  const titleLines = title.split('\n')
  const highlightLines = titleHighlight.split('\n')

  return (
    <section id="hero" data-marketing-section="" className="w-full relative overflow-hidden bg-[#eff4f9] lg:h-[720px]">
      {/* Background photo */}
      <picture>
        <source media="(min-width: 1024px)" srcSet="/marketing/main/main.webp" />
        <img
          src="/marketing/main/main-m.webp"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-bottom pointer-events-none select-none"
        />
      </picture>

      {/* Gradient overlay — desktop: left-to-right, mobile: top-to-bottom */}
      <div
        aria-hidden
        className="absolute inset-0 lg:bg-gradient-to-r lg:from-[#eff4f9] lg:from-[24%] lg:to-[91%] lg:to-transparent bg-gradient-to-b from-[#eff4f9] from-[0%] to-[64%] to-transparent"
      />

      {/* Content */}
      <div className="relative mx-auto max-w-[1136px] px-6 lg:px-0 py-[56px] lg:py-[104px]">
        <div className="flex flex-col items-center lg:items-start gap-6 lg:gap-[56px] max-w-full lg:max-w-[548px]">
          {/* Title */}
          <div className="flex flex-col items-center lg:items-start gap-6 lg:gap-[40px]">
            <div className="flex flex-col items-center lg:items-start">
              {titleLines.map((line, i) => (
                <p
                  key={i}
                  className="marketing-hero-title font-['SB_Aggro',sans-serif] font-bold text-[48px] lg:text-[64px] leading-[1.2] tracking-[-0.6px] text-qb-text-high whitespace-nowrap"
                >
                  <span className="text-qb-primary">{highlightLines[i] ?? ''}</span>
                  {line}
                </p>
              ))}
            </div>

            {/* Body + emphasis */}
            <div className="flex flex-col gap-4 items-center lg:items-start text-center lg:text-left">
              <p className="font-['Pretendard',sans-serif] text-[18px] lg:text-[20px] font-normal leading-[1.5] tracking-[-0.6px] text-qb-text-mid whitespace-pre-line">
                {description}
              </p>
              <p className="font-['Pretendard',sans-serif] text-[20px] lg:text-[24px] font-bold lg:font-semibold leading-[1.4] lg:leading-[1.5] tracking-[-0.6px] text-qb-text-high text-center lg:text-left">
                {emphasis}
              </p>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={onFindCenter}
            className="marketing-cta bg-qb-primary hover:bg-qb-primary-hover active:bg-qb-primary-pressed transition-colors drop-shadow-[0px_4px_4px_rgba(0,0,0,0.1)] h-[48px] lg:h-[56px] px-6 rounded-full text-white font-['Pretendard',sans-serif] text-[18px] lg:text-[20px] font-semibold leading-[1.5] tracking-[-0.6px] cursor-pointer"
          >
            {ctaLabel}
          </button>
        </div>
      </div>

      {/* Spacer to ensure photo is fully visible (mobile only — desktop uses fixed h-720) */}
      <div className="h-[360px] lg:hidden" aria-hidden />
    </section>
  )
}
